import { Controller, useFormContext } from "react-hook-form";
import { Info, MapPin } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "../../../../../../components/input";
import { Button } from "../../../../../../components/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../../../components/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../../../../components/tooltip";
import { VenueSize, VenueSizeLabels } from "../../../data/radius-option";
import type { EventEditorFormValues } from "../event.scheme";
import { renderError, RequiredLabel, SectionTitle, type CommonProps } from "./shared";

type Props = CommonProps & { onOpenLocationPicker: () => void };

export function LocationSection({
  onOpenLocationPicker,
  isSubmitting,
  canEditField,
}: Props) {
  const { control } = useFormContext<EventEditorFormValues>();
  const { t } = useTranslation();
  return (
    <section className="event-form-section">
      <SectionTitle icon={<MapPin className="h-4 w-4" />}>Location</SectionTitle>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Controller
          control={control}
          name="location"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel className="mb-2 block">{t("event.location_address")}</RequiredLabel>
              <div className="flex gap-2">
                <Input
                  {...field}
                  className="min-w-0 flex-1 dark:border-gray-400 dark:bg-gray-800"
                  disabled={isSubmitting || !canEditField("location")}
                  readOnly
                />
                <Button
                  type="button"
                  className="shrink-0 bg-emerald-500 text-white hover:bg-emerald-600"
                  disabled={isSubmitting || !canEditField("location")}
                  onClick={onOpenLocationPicker}
                >
                  <MapPin />
                  <span className="hidden xl:inline">{t("event.choose_location_on_map")}</span>
                </Button>
              </div>
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
        <Controller
          control={control}
          name="radius"
          render={({ field, fieldState }) => (
            <div>
              <RequiredLabel className="flex items-center gap-2">
                {t("event.checkin_radius")}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info
                        className="h-4 w-4 cursor-pointer"
                        color="#99A1AF"
                      />
                    </TooltipTrigger>
                    <TooltipContent sideOffset={5}>
                      Attendees must be within this distance from the event location to check in.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </RequiredLabel>
              <Select
                value={field.value}
                onValueChange={field.onChange}
                disabled={isSubmitting || !canEditField("radius")}
              >
                <SelectTrigger className="dark:border-gray-500 dark:bg-gray-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.values(VenueSize).map((value) => (
                    <SelectItem
                      key={value}
                      value={value}
                    >
                      {VenueSizeLabels[value]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {renderError(fieldState.error?.message)}
            </div>
          )}
        />
      </div>
    </section>
  );
}
